import type {
  AttributionFeeStanding,
  AttributionServiceCase,
  AttributionServiceLogEntry,
  AttributionServiceStage,
} from "@ondo/shared";
import { formatMoney, isSettledStage } from "@ondo/shared";

import { formatDay } from "../lib/dates.ts";
import {
  FEE_STANDING_DETAILS,
  FEE_STANDING_LABELS,
  STAGE_DETAILS,
  STAGE_LABELS,
  STEP_LABELS,
} from "./attributionService.constants.ts";

/**
 * OND-60's cases, turned into something a screen can draw.
 *
 * PURE, AND NOTHING HERE DECIDES ANYTHING THE DATABASE DECIDES. The stage, the
 * effective target, whether a case is overdue and what a fee is standing at are
 * all computed by `ondo.attribution_service_cases()` on every read; this file
 * reads them and writes sentences. Two places deriving "is this overdue" is the
 * drift CLAUDE.md names, and the one that matters is the one with the clock.
 *
 * THE ONE JUDGEMENT IT DOES MAKE is which CONTROLS to offer, and it makes it
 * the way `ClaimCard.canAnswer` does: off facts the server already sent, so a
 * control the database would refuse is never drawn. `iAmTheStudio` and whether
 * the case is settled are both on the row.
 */

/** One case, as the view renders it. */
export interface CaseCard {
  requestId: string;
  workId: string | null;
  title: string;
  /** "Erwin Wurm" — what the claim said, on the day it was made. */
  claimedArtistName: string | null;
  ondoCode: string | null;
  /** The other party, named from whichever side the reader is on. */
  counterparty: string;
  stage: AttributionServiceStage;
  stageLabel: string;
  stageDetail: string;
  /** Still open, so the studio's three acts are available on it. */
  live: boolean;
  overdue: boolean;
  /** "04 Nov 2026", or null where no target was ever written. */
  target: string | null;
  /** Set only when an extension has moved it, so a screen can show both. */
  originalTarget: string | null;
  reviewer: string | null;
  reviewerEscalated: boolean;
  evidenceRequestCount: number;
  extensionCount: number;
  latestEvidenceReason: string | null;
  /** Null until something substantive has been answered. */
  fee: FeeLine | null;
  /**
   * Whether this reader may act on the case at all.
   *
   * OFF `iAmTheStudio` AND THE STAGE, and both come from the server.
   * `ondo.request_attribution_evidence()` and its two siblings are bounded by
   * `ondo.answerable_verification_request_ids()` — `editor` on the studio — so a
   * studio's VIEWER sees these controls and is refused by the database. That is
   * deliberate rather than sloppy: the alternative is to hide the controls from
   * a reviewer who may be about to be promoted, and `me.memberships` is the only
   * thing a client could decide it from — a second copy of a rule the server
   * owns. The refusal is a sentence, not a crash.
   */
  mine: boolean;
  /**
   * Whether the reader is the studio that was asked, LIVE OR SETTLED.
   *
   * `mine` is that AND the case being open, because it gates the three acts;
   * the estate's own screen (OND-212) wants the whole of the studio's side,
   * answered cases included, so it reads this instead. From the server, like
   * `mine`.
   */
  studioSide: boolean;
}

/** The fee, as the ledger line reads. */
export interface FeeLine {
  feeId: string;
  /** "US$250" — `formatMoney`'s own rendering, so one tariff reads one way. */
  amount: string;
  standing: AttributionFeeStanding;
  standingLabel: string;
  standingDetail: string;
  /** Which answer is being charged for. Both are. */
  rejected: boolean;
  paid: string | null;
  paidByName: string | null;
  paidNote: string | null;
  correctionCount: number;
  latestCorrectionReason: string | null;
  /**
   * Whether this reader may record a payment or append a correction.
   *
   * THE STUDIO'S SIDE ONLY, which is `20261005090000` decision 11: both doors
   * require ADMIN on the party owed. The asker is shown the line and no
   * controls — they cannot record their own payment and they certainly cannot
   * void their own obligation.
   */
  mine: boolean;
}

/** One line of the log. */
export interface LogLine {
  id: string;
  label: string;
  /** "04 Nov 2026", on the two kinds that carry a date. */
  target: string | null;
  reason: string | null;
  /** Frozen on the row, so a rename does not rewrite who acted. */
  actedBy: string | null;
  recorded: string;
}

/**
 * The card for one case.
 *
 * `counterparty` IS THE OTHER SIDE, read off `iAmTheStudio`. The studio's queue
 * wants to see who asked; the collector's list wants to see which studio. One
 * field rather than two, because no screen ever wants both and a row that
 * printed both would print the reader back to themselves.
 */
export function toCaseCard(row: AttributionServiceCase): CaseCard {
  const live = !isSettledStage(row.stage);

  return {
    requestId: row.requestId,
    workId: row.workId,
    title: row.workTitle ?? "Untitled",
    claimedArtistName: row.claimedArtistName,
    ondoCode: row.ondoCode,
    counterparty: row.iAmTheStudio
      ? `${row.askerName} · ${row.askerOndoId}`
      : `${row.askedName} · ${row.askedOndoId}`,
    stage: row.stage,
    stageLabel: STAGE_LABELS[row.stage],
    stageDetail: STAGE_DETAILS[row.stage],
    live,
    // FROM THE SERVER AND NOT FROM `Date.now()`. A client clock an hour out
    // would put a red badge on a case that is not late, which is the one thing
    // this label must never do.
    overdue: row.overdue,
    target: row.targetAt ? formatDay(row.targetAt) : null,
    // Shown ONLY where it differs, because an "originally" line repeating the
    // target is noise. `20261005090000` decision 3's point is that both survive.
    originalTarget:
      row.originalTargetAt && row.originalTargetAt !== row.targetAt
        ? formatDay(row.originalTargetAt)
        : null,
    reviewer: row.reviewerName,
    reviewerEscalated: row.reviewerEscalated ?? false,
    evidenceRequestCount: row.evidenceRequestCount,
    extensionCount: row.extensionCount,
    latestEvidenceReason: row.latestEvidenceReason,
    fee: row.fee
      ? {
          feeId: row.fee.feeId,
          amount: formatMoney({ minor: row.fee.amountMinor, currency: row.fee.currency }),
          standing: row.fee.standing,
          standingLabel: FEE_STANDING_LABELS[row.fee.standing],
          standingDetail: FEE_STANDING_DETAILS[row.fee.standing],
          rejected: row.fee.answerStance === "dispute",
          paid: row.fee.paidAt ? formatDay(row.fee.paidAt) : null,
          paidByName: row.fee.paidByName,
          paidNote: row.fee.paidNote,
          correctionCount: row.fee.correctionCount,
          latestCorrectionReason: row.fee.latestCorrectionReason,
          mine: row.iAmTheStudio,
        }
      : null,
    mine: row.iAmTheStudio && live,
    studioSide: row.iAmTheStudio,
  };
}

/** One log line. */
export function toLogLine(row: AttributionServiceLogEntry): LogLine {
  return {
    id: row.id,
    label: STEP_LABELS[row.kind],
    target: row.targetAt ? formatDay(row.targetAt) : null,
    reason: row.reason,
    actedBy: row.actingForName,
    recorded: formatDay(row.recordedAt),
  };
}

/**
 * How many cases are waiting on the READER, for the tab's count.
 *
 * ONLY THE STUDIO'S SIDE COUNTS, and that is the decision. A collector with
 * four cases out has nothing to do about any of them — waiting is not a task —
 * and counting them would put a number on this tab that never went down. The
 * studio's live cases are work.
 */
export function pendingCount(rows: AttributionServiceCase[]): number {
  return rows.filter((row) => row.iAmTheStudio && !isSettledStage(row.stage)).length;
}

/**
 * How many cases are OVERDUE, which is a different question and a sharper one.
 *
 * Counted on both sides, unlike `pendingCount`: a collector whose confirmation
 * is three weeks late does have something to do about it — ask. And nothing in
 * the system will tell them, because nothing expires.
 */
export function overdueCount(rows: AttributionServiceCase[]): number {
  return rows.filter((row) => row.overdue).length;
}

/**
 * What stops an extension being sent, and the sentence that says so.
 *
 * TWO RULES AND NOT THREE. A reason is required, and a date is required. That
 * the date must be LATER than the standing one is NOT checked here, which is
 * deliberate: `targetAt` on the card is a formatted day and the standing target
 * is a row this client may be a minute behind on, so a comparison here would
 * refuse a legitimate extension whenever the two disagreed.
 * `ondo.extend_attribution_target()` compares them and says so.
 */
export function extensionRefusal(input: { targetAt: string; reason: string }): string | null {
  if (input.targetAt.trim().length === 0) return "Name the revised date.";
  if (input.reason.trim().length === 0) return "Say why the target is moving.";

  return null;
}

/** What stops a request for more evidence being sent. */
export function evidenceRefusal(reason: string): string | null {
  if (reason.trim().length === 0) return "Say what you need.";

  return null;
}

/**
 * What stops an assignment being sent.
 *
 * The escalation rule only, which is the one combination invalid on its face.
 * Whether the reviewer is at the studio is the database's to say, with one
 * opaque refusal — a client that could tell "no such id" from "not at this
 * studio" would be a directory of who works where.
 */
export function assignmentRefusal(input: {
  reviewer: string;
  escalate: boolean;
  reason: string;
}): string | null {
  if (input.reviewer.trim().length === 0) return "Name the reviewer by their ONDO id.";
  if (input.escalate && input.reason.trim().length === 0) {
    return "Say why it is being escalated.";
  }

  return null;
}

/** What stops a correction being appended. */
export function correctionRefusal(reason: string): string | null {
  if (reason.trim().length === 0) {
    return "Say why. A correction without a reason is a fee quietly disappearing.";
  }

  return null;
}
